import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, ScrollText } from "lucide-react";

export default function DeliveryLogs() {
  const { activeCompanyId } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [since, setSince] = useState("");

  const load = async () => {
    const params = { limit: 200 };
    if (activeCompanyId) params.company_id = activeCompanyId;
    if (status) params.status = status;
    if (since) params.since = new Date(since).toISOString();
    const { data } = await api.get("/bulk/deliveries", { params });
    setItems(data.items);
    setTotal(data.total);
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [activeCompanyId, status, since]);

  const csv = () => {
    const rows = [["email", "name", "status", "campaign_id", "message_id", "sent_at", "error"]];
    items.forEach((d) => rows.push([d.recipient_email, d.recipient_name || "", d.status, d.campaign_id, d.message_id || "", d.sent_at || "", (d.error_message || "").replace(/,/g, ";")]));
    const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `bulk-deliveries-${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="delivery-log-page">
      <div className="rounded-2xl border border-border bg-white p-3 flex items-center gap-2 flex-wrap">
        <ScrollText className="h-4 w-4 text-muted-foreground" />
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{total} deliveries</span>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-8 border border-border rounded-sm px-2 text-xs ml-2" data-testid="log-status-filter">
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="queued">Queued</option>
        </select>
        <Input type="datetime-local" value={since} onChange={(e) => setSince(e.target.value)} className="h-8 w-56 rounded-sm" data-testid="log-since-filter" />
        <div className="ml-auto"><Button size="sm" variant="outline" onClick={csv} className="rounded-sm" data-testid="log-export-csv"><Download className="h-3.5 w-3.5 mr-1" /> Export CSV</Button></div>
      </div>
      <div className="border border-border rounded-sm overflow-hidden bg-white">
        <table className="tbl w-full" data-testid="delivery-log-table">
          <thead><tr><th>Recipient</th><th>Status</th><th>Campaign</th><th>Sent at</th><th>Error</th></tr></thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} data-testid={`log-row-${d.id}`}>
                <td>
                  <div className="font-medium">{d.recipient_email}</div>
                  <div className="text-[11px] font-mono text-muted-foreground">{d.recipient_name || "—"}</div>
                </td>
                <td><Badge className={`rounded-sm text-[10px] font-mono uppercase ${d.status === "sent" ? "bg-emerald-brand/15 text-emerald-brand" : d.status === "failed" ? "bg-crimson/12 text-crimson" : "bg-sun/25 text-yellow-700"}`}>{d.status}</Badge></td>
                <td><Link to={`/bulk/campaigns/${d.campaign_id}`} className="text-emerald-brand hover:underline text-xs font-mono">{d.campaign_id.slice(0, 8)}…</Link></td>
                <td className="font-mono text-[10px] text-muted-foreground">{d.sent_at?.slice(0, 19).replace("T", " ") || "—"}</td>
                <td className="text-[11px] text-crimson max-w-[240px] truncate" title={d.error_message}>{d.error_message || ""}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="5" className="text-center text-muted-foreground py-8">No deliveries yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
